import React from 'react'
import { View } from 'react-native'
import Svg, { Circle, Defs, LinearGradient, Path, Stop } from 'react-native-svg'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useArcIndicator } from '@src/hooks/useArcIndicator'
import { Text } from '@src/components/base'
const { cos, sin } = Math

const ARC_INDICATOR_PROPERTIES = { width: 170, height: 135, strokeWidth: 17 }
const ARC_WIDTH_PADDING = 15
const ARC_HEIGHT_PADDING = 10
const ARC_COEFFICIENT = 1.3

export interface ActionGoalProgressArcChartProps {
  value?: number
  displayValue?: string | number
  minValue: number
  maxValue: number
}

export const ActionGoalProgressArc = ({
  value = undefined,
  displayValue = undefined,
  minValue,
  maxValue,
}: ActionGoalProgressArcChartProps) => {
  const styles = useStyleSheet(themedStyles)

  const { width, height, strokeWidth } = ARC_INDICATOR_PROPERTIES

  const layoutWidth = width + ARC_WIDTH_PADDING
  const layoutHeight = height + ARC_HEIGHT_PADDING
  const { arcPath, centerX, centerY, startAngle, radius, arcIndicatorAngleSize } = useArcIndicator(
    width,
    height,
    layoutWidth,
    strokeWidth,
    ARC_COEFFICIENT,
  )

  const effectiveValue = value ?? 0
  const clampedValue = Math.max(minValue, Math.min(effectiveValue, maxValue))

  const progress = maxValue === minValue ? 0 : (clampedValue - minValue) / (maxValue - minValue)

  // Calculate the angle corresponding to the current progress
  const angle = startAngle - arcIndicatorAngleSize * progress

  // Calculate the point's position
  const pointX = centerX + radius * cos(angle)
  const pointY = centerY - radius * sin(angle)

  return (
    <View style={styles.container}>
      <Svg width={layoutWidth} height={layoutHeight}>
        <Defs>
          <LinearGradient id="grad" x1="0%" y1="50%" x2="100%" y2="50%">
            <Stop offset="0%" stopColor="#FAC53B" />
            <Stop offset="100%" stopColor="#55BC88" />
          </LinearGradient>
        </Defs>
        <Path
          stroke="url(#grad)"
          fill="none"
          strokeLinecap="round"
          {...{
            strokeWidth,
            d: arcPath,
          }}
        />

        {!!effectiveValue && <Circle cx={pointX} cy={pointY} r={15} fill="white" />}
        {!!effectiveValue && <Circle cx={pointX} cy={pointY} r={10} fill="black" />}
      </Svg>
      <View style={styles.infoContainer}>
        <Text type="score" style={styles.value}>
          {displayValue ?? 'N/A'}
        </Text>
      </View>
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  value: {
    textAlign: 'center',
  },
  infoContainer: {
    position: 'absolute',
    top: 65,
    left: 4,
    right: 0,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
})
